/**
 * 预算调整历史组件
 * 显示项目预算的调整记录和变更详情
 */
import React, { useEffect, useState } from 'react';
import {
  Modal,
  Table,
  Tag,
  Space,
  Typography,
  Timeline,
  Card,
  Row,
  Col,
  Statistic,
  Empty,
  Spin,
  Tooltip,
  Button,
  Pagination
} from 'antd';
import {
  ArrowUpOutlined,
  ArrowDownOutlined,
  SwapOutlined,
  EditOutlined,
  PlusCircleOutlined,
  UserOutlined,
  ClockCircleOutlined
} from '@ant-design/icons';
import type { BudgetAdjustment, BudgetAdjustmentType, BudgetAdjustmentStats } from '../../types';
import { getBudgetHistory, getBudgetStats, formatAdjustmentType, getAdjustmentTypeColor } from '../../services/budgetService';
import dayjs from 'dayjs';

const { Text, Title } = Typography;

interface BudgetHistoryModalProps {
  visible: boolean;
  projectId: string;
  projectName?: string;
  onClose: () => void;
}

// 格式化金额
const formatMoney = (amount: number): string => {
  const prefix = amount >= 0 ? '+' : '';
  return `${prefix}¥${amount.toLocaleString()}`;
};

// 格式化金额（无符号）
const formatMoneyAbs = (amount: number): string => {
  return `¥${Math.abs(amount).toLocaleString()}`;
};

// 获取调整类型图标
const getAdjustmentIcon = (type: string) => {
  switch (type) {
    case 'initial':
      return <PlusCircleOutlined style={{ color: '#1677ff' }} />;
    case 'increase':
      return <ArrowUpOutlined style={{ color: '#52c41a' }} />;
    case 'decrease':
      return <ArrowDownOutlined style={{ color: '#ff4d4f' }} />;
    case 'reallocation':
      return <SwapOutlined style={{ color: '#fa8c16' }} />;
    case 'correction':
      return <EditOutlined style={{ color: '#722ed1' }} />;
    default:
      return <EditOutlined />;
  }
};

const BudgetHistoryModal: React.FC<BudgetHistoryModalProps> = ({
  visible,
  projectId,
  projectName,
  onClose
}) => {
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState<BudgetAdjustment[]>([]);
  const [stats, setStats] = useState<BudgetAdjustmentStats | null>(null);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0 });
  const [viewMode, setViewMode] = useState<'table' | 'timeline'>('table');

  // 加载数据
  const loadData = async () => {
    if (!projectId) return;
    
    setLoading(true);
    try {
      const [historyRes, statsRes] = await Promise.all([
        getBudgetHistory(projectId, { page: pagination.page, limit: pagination.limit }),
        getBudgetStats(projectId)
      ]);
      
      if (historyRes.success) {
        setHistory(historyRes.data || []);
        setPagination(prev => ({
          ...prev,
          total: historyRes.pagination?.total || 0
        }));
      }
      
      if (statsRes.success && statsRes.data) {
        setStats(statsRes.data);
      }
    } catch (error) {
      console.error('加载预算历史失败:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (visible && projectId) {
      loadData();
    }
  }, [visible, projectId, pagination.page]);

  // 表格列定义
  const columns = [
    {
      title: '调整时间',
      dataIndex: 'adjustedAt',
      key: 'adjustedAt',
      width: 160,
      render: (date: string) => (
        <Space>
          <ClockCircleOutlined />
          {dayjs(date).format('YYYY-MM-DD HH:mm')}
        </Space>
      )
    },
    {
      title: '调整类型',
      dataIndex: 'adjustmentType',
      key: 'adjustmentType',
      width: 120,
      render: (type: string) => (
        <Tag icon={getAdjustmentIcon(type)} color={getAdjustmentTypeColor(type)}>
          {formatAdjustmentType(type)}
        </Tag>
      )
    },
    {
      title: '调整原因',
      dataIndex: 'reason',
      key: 'reason',
      ellipsis: true,
      render: (reason: string, record: BudgetAdjustment) => (
        <Tooltip title={record.description || reason}>
          <Text>{reason}</Text>
        </Tooltip>
      )
    },
    {
      title: '变化金额',
      dataIndex: 'changes',
      key: 'totalChange',
      width: 140,
      align: 'right' as const,
      render: (_: any, record: BudgetAdjustment) => {
        const change = record.changes?.total || (record.newTotal - record.previousTotal);
        return (
          <Text style={{ color: change >= 0 ? '#52c41a' : '#ff4d4f' }}>
            {formatMoney(change)}
          </Text>
        );
      }
    },
    {
      title: '调整后总额',
      dataIndex: 'newTotal',
      key: 'newTotal',
      width: 140,
      align: 'right' as const,
      render: (total: number) => <Text strong>{formatMoneyAbs(total)}</Text>
    },
    {
      title: '操作人',
      dataIndex: 'adjustedBy',
      key: 'adjustedBy',
      width: 120,
      render: (user: any) => (
        <Space>
          <UserOutlined />
          {user?.realName || user?.username || '-'}
        </Space>
      )
    }
  ];

  // 渲染时间线视图
  const renderTimeline = () => (
    <Timeline mode="left">
      {history.map((item, index) => (
        <Timeline.Item
          key={item._id}
          color={getAdjustmentTypeColor(item.adjustmentType)}
          label={dayjs(item.adjustedAt).format('YYYY-MM-DD HH:mm')}
        >
          <Card size="small" style={{ marginBottom: 8 }}>
            <Space orientation="vertical" style={{ width: '100%' }}>
              <div>
                <Tag icon={getAdjustmentIcon(item.adjustmentType)} color={getAdjustmentTypeColor(item.adjustmentType)}>
                  {formatAdjustmentType(item.adjustmentType)}
                </Tag>
                <Text type="secondary" style={{ marginLeft: 8 }}>
                  by {item.adjustedBy?.realName || item.adjustedBy?.username}
                </Text>
              </div>
              <Text>{item.reason}</Text>
              {item.description && (
                <Text type="secondary" style={{ fontSize: 12 }}>{item.description}</Text>
              )}
              <Row gutter={16}>
                <Col span={8}>
                  <Text type="secondary">调整前: </Text>
                  <Text>{formatMoneyAbs(item.previousTotal)}</Text>
                </Col>
                <Col span={8}>
                  <Text type="secondary">变化: </Text>
                  <Text style={{ color: item.changes?.total >= 0 ? '#52c41a' : '#ff4d4f' }}>
                    {formatMoney(item.changes?.total || 0)}
                  </Text>
                </Col>
                <Col span={8}>
                  <Text type="secondary">调整后: </Text>
                  <Text strong>{formatMoneyAbs(item.newTotal)}</Text>
                </Col>
              </Row>
            </Space>
          </Card>
        </Timeline.Item>
      ))}
    </Timeline>
  );

  return (
    <Modal
      title={`预算调整历史 - ${projectName || '项目'}`}
      open={visible}
      onCancel={onClose}
      width={900}
      footer={null}
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Spin size="large" />
        </div>
      ) : (
        <>
          {/* 统计概览 */}
          {stats && (
            <Card size="small" style={{ marginBottom: 16, backgroundColor: '#f5f5f5' }}>
              <Row gutter={16}>
                <Col span={6}>
                  <Statistic
                    title="调整次数"
                    value={stats.totalAdjustments}
                    suffix="次"
                  />
                </Col>
                <Col span={6}>
                  <Statistic
                    title="净变化"
                    value={Math.abs(stats.netChange)}
                    prefix={stats.netChange >= 0 ? '+¥' : '-¥'}
                    valueStyle={{ color: stats.netChange >= 0 ? '#52c41a' : '#ff4d4f' }}
                  />
                </Col>
                <Col span={6}>
                  <Statistic
                    title="首次调整"
                    value={stats.firstAdjustment ? dayjs(stats.firstAdjustment).format('MM-DD') : '-'}
                  />
                </Col>
                <Col span={6}>
                  <Statistic
                    title="最近调整"
                    value={stats.lastAdjustment ? dayjs(stats.lastAdjustment).format('MM-DD') : '-'}
                  />
                </Col>
                <Col span={6}>
                  <Statistic
                    title="预算版本"
                    value={`V${stats.currentVersion || 1}`}
                  />
                </Col>
              </Row>
              {typeof stats.revenueTotal === 'number' && (
                <div style={{ marginTop: 12 }}>
                  <Text type="secondary">累计收入：</Text>
                  <Text strong style={{ color: '#1677ff' }}>¥{stats.revenueTotal.toLocaleString()}</Text>
                  {typeof stats.versionCount === 'number' && (
                    <Text type="secondary" style={{ marginLeft: 12 }}>共 {stats.versionCount} 个版本</Text>
                  )}
                </div>
              )}
            </Card>
          )}

          {/* 视图切换 */}
          <div style={{ marginBottom: 16, textAlign: 'right' }}>
            <Space>
              <Button
                type={viewMode === 'table' ? 'primary' : 'default'}
                onClick={() => setViewMode('table')}
              >
                表格视图
              </Button>
              <Button
                type={viewMode === 'timeline' ? 'primary' : 'default'}
                onClick={() => setViewMode('timeline')}
              >
                时间线视图
              </Button>
            </Space>
          </div>

          {/* 历史记录 */}
          {history.length === 0 ? (
            <Empty description="暂无调整记录" />
          ) : viewMode === 'table' ? (
            <>
              <Table
                columns={columns}
                dataSource={history}
                rowKey="_id"
                pagination={false}
                size="small"
                scroll={{ x: 800 }}
              />
              <div style={{ marginTop: 16, textAlign: 'right' }}>
                <Pagination
                  current={pagination.page}
                  pageSize={pagination.limit}
                  total={pagination.total}
                  onChange={(page) => setPagination(prev => ({ ...prev, page }))}
                  showSizeChanger={false}
                  showTotal={(total) => `共 ${total} 条记录`}
                />
              </div>
            </>
          ) : (
            renderTimeline()
          )}
        </>
      )}
    </Modal>
  );
};

export default BudgetHistoryModal;
